import { ClearOutlined, CloudUploadOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  DatePicker,
  Flex,
  InputNumber,
  message,
  Modal,
  Table,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table/interface'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js'
import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'

import { AppAdmin } from 'api/app-admin'
import { FEATURE } from 'constants/feature'
import { usePTOV2Context } from 'context/pto-v2-context-provider'
import { usePermission } from 'hooks/use-permission'
import { logError } from 'utils/logger'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

/** Constraints from Confluence 5.4 "PTO Details - tab Market". */
// Label format used by the market-chart API (e.g. "Dec 2024").
const MONTH_FORMAT = 'MMM YYYY'
// Legacy label format from the initial API doc (e.g. "01/2025") — still accepted when parsing.
const MONTH_FORMAT_LEGACY = 'MM/YYYY'
const MIN_COLUMNS = 1
const MAX_COLUMNS = 10
const VALUE_MAX_LENGTH = 20
const VALUE_DECIMALS = 3

interface MarketColumnDraft {
  month: Dayjs | null
  value: number | null
}

const EMPTY_COLUMN: MarketColumnDraft = { month: null, value: null }

/** Round to the allowed number of decimals (spec: decimal 3). */
const roundValue = (value: number) => {
  const factor = 10 ** VALUE_DECIMALS
  return Math.round(value * factor) / factor
}

const formatValue = (value?: number | null) =>
  value == null ? '' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(value)

const parseMonthLabel = (label: string) => {
  const parsed = dayjs(label, [MONTH_FORMAT, MONTH_FORMAT_LEGACY])
  return parsed.isValid() ? parsed : null
}

const Market = () => {
  const { code } = usePTOV2Context()
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.PTO)

  // Backing store of drafts — only ever grows, so shrinking the column count keeps entered data.
  const [columns, setColumns] = useState<MarketColumnDraft[]>([{ ...EMPTY_COLUMN }])
  // Number of columns actually shown/published (first `count` items of `columns`).
  const [count, setCount] = useState(1)
  const [publishedAt, setPublishedAt] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)
  const [publishing, setPublishing] = useState(false)

  const getMarketChart = useCallback(async () => {
    if (!code) return
    try {
      setLoading(true)
      const res = await AppAdmin.getPtoMarketChart(code)
      const payload = res.data?.data
      const saved = (payload?.columns ?? []) as { label: string; value: number }[]

      setPublishedAt(payload?.publishedAt)
      if (saved.length) {
        const drafts = saved.slice(0, MAX_COLUMNS).map((col) => ({
          month: parseMonthLabel(col.label),
          value: col.value ?? null,
        }))
        setColumns(drafts)
        setCount(drafts.length)
      }
    } catch (error) {
      logError('Get PTO market chart failed', error)
    } finally {
      setLoading(false)
    }
  }, [code])

  useEffect(() => {
    getMarketChart()
  }, [getMarketChart])

  // Resize the visible column count. The backing list is only grown, never truncated,
  // so intermediate keystrokes (e.g. "10" -> "1" -> "5") don't wipe entered data.
  const handleCountChange = (value: number | null) => {
    if (!value) return
    const next = Math.min(Math.max(Math.trunc(value), MIN_COLUMNS), MAX_COLUMNS)
    setCount(next)
    setColumns((prev) =>
      prev.length >= next
        ? prev
        : [...prev, ...Array.from({ length: next - prev.length }, () => ({ ...EMPTY_COLUMN }))]
    )
  }

  const updateColumn = (index: number, patch: Partial<MarketColumnDraft>) => {
    setColumns((prev) => prev.map((col, i) => (i === index ? { ...col, ...patch } : col)))
  }

  const handleClearValues = () => {
    setColumns((prev) => prev.map(() => ({ ...EMPTY_COLUMN })))
  }

  const visibleColumns = columns.slice(0, count)

  const handleOpenPublish = () => {
    const incomplete = visibleColumns.some((col) => !col.month || col.value == null)
    if (incomplete) {
      message.error('Please fill in month and value for all columns before publishing')
      return
    }
    setPublishOpen(true)
  }

  const handleConfirmPublish = async () => {
    if (!code) return
    try {
      setPublishing(true)
      await AppAdmin.updatePtoMarketChart(code, {
        columns: visibleColumns.map((col) => ({
          label: col.month!.format(MONTH_FORMAT),
          value: col.value ?? 0,
        })),
      })
      message.success('Market chart published to site')
      setPublishOpen(false)
      getMarketChart()
    } catch (error: any) {
      logError('Publish PTO market chart failed', error)
      message.error(error?.response?.data?.message || 'Failed to publish market chart')
    } finally {
      setPublishing(false)
    }
  }

  // Preview only the columns that already have both month and value.
  const previewColumns = visibleColumns.filter((col) => col.month && col.value != null)

  const chartData = {
    labels: previewColumns.map((col) => col.month!.format(MONTH_FORMAT)),
    datasets: [
      {
        label: 'Price',
        data: previewColumns.map((col) => col.value ?? 0),
        backgroundColor: '#36A2EB',
        borderColor: '#36A2EB',
        borderWidth: 1,
      },
    ],
  }

  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true } },
  }

  const summaryColumns: ColumnsType<MarketColumnDraft> = [
    {
      title: 'No.',
      key: 'no',
      align: 'center',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Month',
      key: 'month',
      render: (_, record) => record.month?.format(MONTH_FORMAT) ?? '-',
    },
    {
      title: 'Value',
      key: 'value',
      align: 'right',
      render: (_, record) => formatValue(record.value),
    },
  ]

  return (
    <Flex vertical gap={16}>
      <Card
        loading={loading}
        title="Market Chart Config"
        extra={
          publishedAt && (
            <Typography.Text type="secondary">
              Last published: {dayjs(publishedAt).format('DD/MM/YYYY HH:mm')}
            </Typography.Text>
          )
        }
      >
        <Flex vertical gap={16}>
          <Flex align="center" gap={12}>
            <Typography.Text strong>Number of columns:</Typography.Text>
            <InputNumber
              min={MIN_COLUMNS}
              max={MAX_COLUMNS}
              precision={0}
              value={count}
              onChange={handleCountChange}
              disabled={!canEdit}
              style={{ width: 120 }}
            />
          </Flex>

          <Flex gap={12} wrap="wrap">
            {visibleColumns.map((col, index) => (
              <Card key={index} size="small" style={{ width: 220 }}>
                <Flex vertical gap={8}>
                  <Typography.Text type="secondary">Column {index + 1}</Typography.Text>
                  <DatePicker
                    picker="month"
                    format={MONTH_FORMAT}
                    placeholder="Select month"
                    value={col.month}
                    onChange={(value) => updateColumn(index, { month: value })}
                    disabled={!canEdit}
                    style={{ width: '100%' }}
                  />
                  <InputNumber
                    placeholder="0"
                    min={0}
                    value={col.value}
                    onChange={(value) =>
                      updateColumn(index, { value: value == null ? null : roundValue(value) })
                    }
                    formatter={(value) =>
                      value == null || value === ''
                        ? ''
                        : `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(value) => (value ? value.replace(/,/g, '') : '') as any}
                    maxLength={VALUE_MAX_LENGTH}
                    disabled={!canEdit}
                    style={{ width: '100%' }}
                  />
                </Flex>
              </Card>
            ))}
          </Flex>

          {canEdit && (
            <Flex gap={12}>
              <Button icon={<ClearOutlined />} onClick={handleClearValues}>
                Clear values
              </Button>
              <Button type="primary" icon={<CloudUploadOutlined />} onClick={handleOpenPublish}>
                Publish to site
              </Button>
            </Flex>
          )}
        </Flex>
      </Card>

      <Card title="Preview">
        {previewColumns.length ? (
          <div style={{ width: '100%', height: 360 }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        ) : (
          <Typography.Text type="secondary">
            Enter month and value to preview the chart
          </Typography.Text>
        )}
      </Card>

      <Modal
        open={publishOpen}
        centered
        title="Publish market chart"
        okText="Confirm Publish"
        confirmLoading={publishing}
        onOk={handleConfirmPublish}
        onCancel={() => (publishing ? undefined : setPublishOpen(false))}
      >
        <Typography.Paragraph>
          The chart below will be published to the PTO details screen on the user website.
        </Typography.Paragraph>
        <Table
          columns={summaryColumns}
          dataSource={visibleColumns}
          rowKey={(_, index) => `${index}`}
          pagination={false}
          size="small"
          bordered
        />
      </Modal>
    </Flex>
  )
}

export default Market
